import { createContext, ReactNode, useContext } from 'react';
import { useParams } from 'react-router';
import { Client } from 'src/admin/users/types';
import { useClient } from '../hooks/useClient';

type CurrentClient = {
  clientId: string;
  client: Client | null;
  refetch: ReturnType<typeof useClient>['refetch'];
  isLoading: boolean;
  isClientPage: boolean;
};

type CurrentClientContextType = {
  isEnabled: true;
};

const CurrentClientContext = createContext<CurrentClientContextType | undefined>(undefined);

type CurrentClientProviderProps = {
  children: ReactNode;
};

export function CurrentClientProvider({ children }: CurrentClientProviderProps) {
  return <CurrentClientContext value={{ isEnabled: true }}>{children}</CurrentClientContext>;
}

export function useCurrentClientIfAvailable() {
  const context = useContext(CurrentClientContext);
  if (context === undefined) {
    throw new Error('useCurrentClientIfAvailable must be used within a CurrentClientProvider');
  }

  const { clientId: clientIdParam } = useParams<{ clientId: string }>();
  const clientId = clientIdParam;
  const hasClientId = clientId != null && clientId !== 'undefined';

  const { client, isLoading, refetch } = useClient(hasClientId ? clientId : undefined);

  return {
    isLoading,
    refetch,
    isClientPage: clientIdParam != null,
    clientId,
    client: hasClientId ? client : null,
  };
}

export function useCurrentClient(): CurrentClient {
  const data = useCurrentClientIfAvailable();

  if (hasClientId(data)) {
    return data as CurrentClient;
  }

  throw new Error('Client ID is null');
}

function hasClientId(data: Partial<CurrentClient>): data is CurrentClient {
  return data.clientId != null;
}
